/**
 * The course builder's wizard (docs/plans/lms-course-builder-plan.md 8.1,
 * 8.2, 6.2, 6.10, 7.3, 9.2, 18.2; WP-B30), the route element at
 * /lms/manage/catalog/build (?course=<id> opens it in extend mode). Four
 * steps on one page, no modal: Upload, Steer, Estimate, then Start, which
 * hands over to the run page at catalog/runs/:runId.
 *
 * - Nothing renders until GET /health says the builder is on for the
 *   caller's network (G20); otherwise the page says so and links back.
 * - GET /admin/builder/config gives the live limits (files, bytes), the source
 *   minimum per category and the caps; the constants of api/builder.ts stand
 *   in when it cannot be read (the server re-checks every one).
 * - Extend mode reads the course, its versions and the current version: a
 *   course with no published version, or with an open draft, cannot be
 *   extended (6.10, the createDraft rule), and the page says why with a link.
 * - Leaving Steer creates the run (POST /admin/builder/runs, multipart); going
 *   back from Estimate cancels that run first, so no stray estimated run is
 *   left behind, and the next estimate is a new run.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader2, Sparkles } from 'lucide-react';
import PageHeader from '../../../../../ui/PageHeader';
import { useToast } from '../../../../../ui/Toast';
import { outlineBtnCls, primaryBtnCls, secondaryBtnCls } from '../../../../../ui/styles';
import {
  BUILDER_DEFAULT_MAX_FILES,
  BUILDER_FILE_MAX_BYTES,
  cancelBuilderRun,
  createBuilderRun,
  getBuilderConfig,
  readBuilderError,
} from '../../../../api/builder';
import type { BuilderConfig, BuilderRun, BuilderSourceMeta, CreateRunInput } from '../../../../api/builder';
import { getVersion, listCourseVersions } from '../../../../api/catalog';
import { apiErrorMessage } from '../../../../api/client';
import { getCourse } from '../../../../api/courses';
import type { CourseDetail } from '../../../../api/courses';
import { noticeCls } from '../../../../components/player/styles';
import { catalogPaths } from '../model';
import { linkCls } from '../styles';
import { EstimateStep } from './EstimateStep';
import { builderPaths, explainError, isClosingSectionTitle, isScenarioCategory, sourceMinimum } from './runModel';
import { blankSteerExtend, blankSteerNew, SteerStep } from './SteerStep';
import type { SectionChoice, SteerExtend, SteerNew } from './SteerStep';
import { UploadStep, uploadProblems } from './UploadStep';
import { useBuilderAvailability } from './useBuildRun';
import type { CitationEntry, LinkEntry, UploadEntry } from './UploadStep';

type Step = 'upload' | 'steer' | 'estimate';

const STEPS: Array<{ key: Step | 'build'; label: string }> = [
  { key: 'upload', label: 'Upload' },
  { key: 'steer', label: 'Steer' },
  { key: 'estimate', label: 'Estimate' },
  { key: 'build', label: 'Build' },
];

interface ExtendState {
  course: CourseDetail;
  sections: SectionChoice[];
}

function StepList({ step }: { step: Step }) {
  const at = STEPS.findIndex((s) => s.key === step);
  return (
    <ol className="flex flex-wrap items-center gap-2 text-xs" aria-label="Steps" data-testid="lms-builder-steps">
      {STEPS.map((s, i) => {
        const current = i === at;
        const done = i < at;
        const cls = current
          ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
          : done
            ? 'border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300'
            : 'border-gray-200 text-gray-400 dark:border-gray-700 dark:text-gray-500';
        return (
          <li
            key={s.key}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-medium ${cls}`}
            aria-current={current ? 'step' : undefined}
          >
            <span className="tabular-nums">{i + 1}</span>
            {s.label}
            {done ? <span className="sr-only">(done)</span> : null}
          </li>
        );
      })}
    </ol>
  );
}

function sourcesMetaOf(files: UploadEntry[]): BuilderSourceMeta[] {
  const out: BuilderSourceMeta[] = [];
  files.forEach((f, i) => {
    const title = f.title.trim();
    const url = f.publicUrl.trim();
    if (!title && !url) return;
    const meta: BuilderSourceMeta = { ordinal: i + 1 };
    if (title) meta.title = title;
    if (url) meta.public_url = url;
    out.push(meta);
  });
  return out;
}

export function BuildWizard() {
  const [params] = useSearchParams();
  const courseId = params.get('course');
  const mode: 'new' | 'extend' = courseId ? 'extend' : 'new';
  const navigate = useNavigate();
  const { toast } = useToast();
  const availability = useBuilderAvailability();

  const [config, setConfig] = useState<BuilderConfig | null>(null);
  const [extend, setExtend] = useState<ExtendState | null>(null);
  const [extendBlock, setExtendBlock] = useState<{ message: string; draftId?: string } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [step, setStep] = useState<Step>('upload');
  const [files, setFiles] = useState<UploadEntry[]>([]);
  const [links, setLinks] = useState<LinkEntry[]>([]);
  const [citations, setCitations] = useState<CitationEntry[]>([]);
  const [attested, setAttested] = useState(false);
  const [steerNew, setSteerNew] = useState<SteerNew>(blankSteerNew);
  const [steerExtend, setSteerExtend] = useState<SteerExtend>(blankSteerExtend);
  const [showProblems, setShowProblems] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [runId, setRunId] = useState<string | null>(null);

  useEffect(() => {
    if (!availability.available) return undefined;
    let cancelled = false;
    getBuilderConfig()
      .then((c) => {
        if (!cancelled) setConfig(c);
      })
      .catch(() => {
        // The constants stand in; the server re-checks every limit.
      });
    return () => {
      cancelled = true;
    };
  }, [availability.available]);

  useEffect(() => {
    if (!availability.available || mode !== 'extend' || !courseId) return undefined;
    let cancelled = false;
    setLoadError(null);
    setExtendBlock(null);
    Promise.all([getCourse(courseId), listCourseVersions(courseId)])
      .then(async ([course, versions]) => {
        if (cancelled) return;
        const draft = versions.find((v) => v.status === 'draft');
        if (draft) {
          setExtendBlock({
            message: `${course.title} has an open draft (version ${draft.version_no}). Publish or discard it before adding content: the builder writes its own new draft.`,
            draftId: draft.id,
          });
          return;
        }
        if (!course.current_version) {
          setExtendBlock({ message: `${course.title} has no published version yet. Publish a first version before adding content to it.` });
          return;
        }
        const detail = await getVersion(course.current_version.id);
        if (cancelled) return;
        const sections: SectionChoice[] = (detail.content?.sections || []).map((s) => ({
          id: s.id,
          title: s.title,
          closing: isClosingSectionTitle(s.title),
        }));
        setExtend({ course, sections });
      })
      .catch((err) => {
        if (!cancelled) setLoadError(apiErrorMessage(err, 'The course could not be loaded.'));
      });
    return () => {
      cancelled = true;
    };
  }, [availability.available, mode, courseId]);

  const category = mode === 'extend' ? extend?.course.category ?? null : steerNew.category;
  const maxFiles = config?.limits?.max_files ?? BUILDER_DEFAULT_MAX_FILES;
  const maxBytes = config?.limits?.max_bytes_per_file ?? BUILDER_FILE_MAX_BYTES;
  const minimum = sourceMinimum(category, config?.source_minimum ?? null);
  const problems = useMemo(
    () => uploadProblems({ files, links, citations, attested, maxFiles, maxBytes }),
    [files, links, citations, attested, maxFiles, maxBytes]
  );
  const sourceCount = files.length + links.length + citations.length;

  const title = mode === 'extend' ? (extend ? `Add content to ${extend.course.title}` : 'Add content to a course') : 'Build a course from documents';
  const back = mode === 'extend' && courseId ? catalogPaths.course(courseId) : catalogPaths.list();
  const header = (
    <PageHeader
      title={title}
      icon={Sparkles}
      subtitle="The builder writes a draft from your documents. Nothing is published until a person reads it and publishes it."
      actions={
        <Link to={back} className={outlineBtnCls()} data-testid="lms-builder-back-link">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {mode === 'extend' ? 'Back to the course' : 'Back to the catalog'}
        </Link>
      }
    />
  );

  if (!availability.checked) {
    return (
      <div className="space-y-4" data-testid="lms-builder-wizard">
        {header}
        <div className="flex items-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400" role="status">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
          Checking the course builder...
        </div>
      </div>
    );
  }
  if (!availability.available) {
    return (
      <div className="space-y-4" data-testid="lms-builder-wizard">
        {header}
        <div className={noticeCls('amber')} role="status" data-testid="lms-builder-unavailable">
          The course builder is not switched on for your network. Courses can still be written by hand from the catalog.
        </div>
      </div>
    );
  }

  const createRun = async () => {
    if (creating) return;
    setCreateError(null);
    if (sourceCount < minimum) {
      setCreateError(
        `This category needs at least ${minimum} sources (files, web links and citations together); there ${sourceCount === 1 ? 'is' : 'are'} ${sourceCount}. Go back to Upload and add more.`
      );
      return;
    }
    const common = {
      files: files.map((f) => f.file),
      sourcesMeta: sourcesMetaOf(files),
      extraSources: citations,
      sourceLinks: links.map((l) => (l.title ? { url: l.url, title: l.title } : { url: l.url })),
      attestation: attested,
    };
    let input: CreateRunInput;
    if (mode === 'extend') {
      if (!extend || !courseId) return;
      input = {
        mode: 'extend',
        course_id: courseId,
        replace_section_ids: steerExtend.replaceSectionIds,
        keep_closing: !steerExtend.reviseClosing,
        audience: steerExtend.audience,
        instruction: steerExtend.instruction,
        ...common,
      };
    } else {
      const scenario = isScenarioCategory(steerNew.category);
      input = {
        mode: 'new',
        title: steerNew.title,
        category: steerNew.category,
        kind: scenario ? 'quiz' : steerNew.kind,
        validity_months: scenario ? null : steerNew.validityMonths,
        renewal_lead_days: scenario ? null : steerNew.renewalLeadDays,
        audience: steerNew.audience,
        target_minutes: steerNew.targetMinutes,
        pool_size: steerNew.poolSize,
        instruction: steerNew.instruction,
        ...common,
      };
    }
    setCreating(true);
    try {
      const run = await createBuilderRun(input);
      setRunId(run.id);
      setStep('estimate');
    } catch (err) {
      const problem = readBuilderError(err, 'The files could not be sent.');
      setCreateError(explainError(problem.code, problem.message));
    } finally {
      setCreating(false);
    }
  };

  const backFromEstimate = async () => {
    const id = runId;
    setRunId(null);
    setStep('steer');
    if (!id) return;
    try {
      await cancelBuilderRun(id);
    } catch {
      // A run already failed or cancelled needs nothing; any other answer leaves an estimated run nobody starts.
    }
  };

  const onStarted = (run: BuilderRun) => {
    toast('The build has started. This page shows its progress; you can leave it and come back.', { type: 'info' });
    navigate(builderPaths.run(run.id));
  };

  const extendReady = mode === 'new' || Boolean(extend);

  return (
    <div className="space-y-4" data-testid="lms-builder-wizard" data-mode={mode}>
      {header}
      <StepList step={step} />

      {loadError ? (
        <div className={noticeCls('red')} role="alert" data-testid="lms-builder-load-error">
          {loadError}
        </div>
      ) : null}

      {extendBlock ? (
        <div className={noticeCls('amber')} role="status" data-testid="lms-builder-extend-blocked">
          <span>
            {extendBlock.message}{' '}
            {extendBlock.draftId ? (
              <Link to={catalogPaths.version(extendBlock.draftId)} className={linkCls}>
                Open the draft
              </Link>
            ) : null}
          </span>
        </div>
      ) : null}

      {mode === 'extend' && !extend && !extendBlock && !loadError ? (
        <div className="flex items-center gap-2 py-6 text-sm text-gray-500 dark:text-gray-400" role="status">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
          Loading the course...
        </div>
      ) : null}

      {extendReady && step === 'upload' ? (
        <>
          <UploadStep
            files={files}
            onFiles={setFiles}
            links={links}
            onLinks={setLinks}
            citations={citations}
            onCitations={setCitations}
            attested={attested}
            onAttested={setAttested}
            maxFiles={maxFiles}
            maxBytes={maxBytes}
            minimum={minimum}
            category={category}
          />
          {showProblems && problems.length > 0 ? (
            <ul className={`${noticeCls('red')} flex-col`} role="alert" data-testid="lms-builder-upload-problems">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          ) : null}
          <div className="flex justify-end border-t border-gray-100 pt-4 dark:border-gray-700">
            <button
              type="button"
              className={primaryBtnCls}
              onClick={() => {
                setShowProblems(true);
                if (problems.length === 0) setStep('steer');
              }}
              data-testid="lms-builder-next"
            >
              Next: steer the course
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </>
      ) : null}

      {extendReady && step === 'steer' ? (
        <>
          {mode === 'extend' && extend ? (
            <SteerStep
              mode="extend"
              value={steerExtend}
              onChange={setSteerExtend}
              courseTitle={extend.course.title}
              category={extend.course.category}
              sections={extend.sections}
            />
          ) : (
            <SteerStep mode="new" value={steerNew} onChange={setSteerNew} />
          )}
          {createError ? (
            <div className={noticeCls('red')} role="alert" data-testid="lms-builder-create-error">
              {createError}
            </div>
          ) : null}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-4 dark:border-gray-700">
            <button type="button" className={secondaryBtnCls} onClick={() => setStep('upload')} disabled={creating} data-testid="lms-builder-steer-back">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Back to the files
            </button>
            <button type="button" className={primaryBtnCls} onClick={createRun} disabled={creating} data-testid="lms-builder-get-estimate">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <ArrowRight className="h-4 w-4" aria-hidden="true" />}
              {creating ? 'Sending the files...' : 'Send the files and get the estimate'}
            </button>
          </div>
        </>
      ) : null}

      {step === 'estimate' && runId ? <EstimateStep runId={runId} onStarted={onStarted} onBack={backFromEstimate} /> : null}
    </div>
  );
}

export default BuildWizard;
